import type { ReactNode } from 'react';
import { fill } from '../i18n/shell-texts';
import { useShellTexts } from '../i18n/use-shell-texts';
import { buttonClass } from '../kit/button-class';
import { errorClass, InputGroup, labelClass } from '../kit/form/input';
import { usePortalBranding } from '../shell/branding';

/**
 * The frame every pre-login page shares: the legacy login's card group, centred on the page
 * background — the form card with the brand's logo, and (from `lg`, when the brand has a sign-up
 * link) a green pane inviting dealers to get in touch.
 */
export function AuthCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  /** A muted line under the title. */
  subtitle?: string;
  children: ReactNode;
}) {
  const t = useShellTexts().login;
  const branding = usePortalBranding();
  const signUp = branding.signUpUrl;
  return (
    <main className="flex min-h-dvh items-center justify-center bg-page px-4 py-8">
      <div className="flex w-full max-w-[760px]">
        <div
          className={`min-w-0 flex-1 rounded-bs border border-line bg-white p-2 ${signUp ? 'lg:rounded-r-none' : ''}`}
        >
          <div className="p-5">
            {branding.logo ? <div className="mb-[30px] w-[150px]">{branding.logo}</div> : null}
            <h1 className="mb-1 text-2xl">{title}</h1>
            {subtitle ? <p className="mb-4 text-ink-muted">{subtitle}</p> : null}
            {children}
          </div>
        </div>
        {signUp ? (
          <aside className="hidden w-[44%] shrink-0 items-center rounded-r-bs border border-brand-border bg-brand py-3 text-center text-white lg:flex">
            <div className="w-full p-5">
              <h2 className="mb-2 text-[1.75rem]">{t.signUpTitle}</h2>
              <p className="mb-4">{fill(t.signUpText, { brand: branding.name })}</p>
              <a
                href={signUp}
                target="_blank"
                rel="noopener noreferrer"
                className={`${buttonClass({ variant: 'primary' })} border-brand-border bg-brand-hover`}
              >
                {t.signUpAction}
              </a>
            </div>
          </aside>
        ) : null}
      </div>
    </main>
  );
}

/**
 * A labelled control with its error. `addon` joins a box to the control's left (legacy `@` and
 * lock); `hideLabel` keeps the label for assistive technology only, as the legacy login showed
 * none.
 */
export function Field({
  id,
  label,
  error,
  addon,
  hideLabel = false,
  children,
}: {
  id: string;
  label: string;
  error?: string | null;
  addon?: ReactNode;
  hideLabel?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="mb-4">
      <label htmlFor={id} className={hideLabel ? 'sr-only' : labelClass}>
        {label}
      </label>
      {addon !== undefined ? <InputGroup before={addon}>{children}</InputGroup> : children}
      {error ? (
        <p id={`${id}-error`} className={errorClass}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** The kit's alert; kept under this path for the pre-login pages. */
export { Alert } from '../kit/alert';
